import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Radio, ChevronDown, ChevronUp, Clock } from 'lucide-react';

/**
 * Live indicator strip shown on the Wallet page whenever the current user has
 * any withdrawals in the 'pending' state. Polls /api/wallet/withdrawals/pending
 * every 20s + on window focus. Collapsed by default (compact strip); expandable
 * to a per-request breakdown with queue position + ETA.
 */
export default function PendingWithdrawalsLive() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/wallet/withdrawals/pending');
      setData(r.data);
    } catch (_e) { /* silent — user may not be logged in */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, []);

  if (!data || data.pending_count === 0) return null;

  const fmtEta = (m) => {
    if (m == null) return '—';
    if (m < 1) return '<1m';
    if (m < 60) return `~${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    return `~${h}h ${rem}m`;
  };

  return (
    <div className="border border-[#F0B90B]/40 bg-[#F0B90B]/10 rounded-sm" data-testid="pending-withdrawals-live">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left"
        data-testid="pending-withdrawals-toggle"
      >
        <span className="relative inline-flex shrink-0">
          <span className="absolute inset-0 rounded-full bg-[#F6465D]/60 animate-ping" />
          <Radio className="w-4 h-4 text-[#F6465D] relative" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#F6465D] font-bold">LIVE</span>
        <div className="flex-1 min-w-0 text-sm">
          <span className="text-white font-semibold">{data.pending_count} withdrawal{data.pending_count === 1 ? '' : 's'} in review</span>
          <span className="text-[#B7BDC6] hidden sm:inline ml-2">
            · Queue depth: <span className="font-mono text-white">{data.queue_depth}</span>
            {data.avg_processing_minutes != null && (
              <> · Avg processing: <span className="font-mono text-white">{fmtEta(data.avg_processing_minutes)}</span></>
            )}
          </span>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#848E9C]" /> : <ChevronDown className="w-4 h-4 text-[#848E9C]" />}
      </button>
      {open && (
        <div className="border-t border-[#F0B90B]/30 divide-y divide-[#F0B90B]/20" data-testid="pending-withdrawals-list">
          {data.pending.map(row => (
            <div key={row.request_id} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center text-xs" data-testid={`pending-row-${row.request_id}`}>
              <div className="col-span-3 min-w-0">
                <div className="text-white font-semibold uppercase">{row.amount} {row.symbol}</div>
                <div className="text-[#848E9C] font-mono">${Number(row.amount_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="col-span-4 min-w-0">
                <div className="text-[10px] text-[#848E9C] uppercase tracking-wider">To</div>
                <div className="text-[#EAECEF] font-mono truncate">{row.address}</div>
              </div>
              <div className="col-span-2 text-center">
                <div className="text-[10px] text-[#848E9C] uppercase tracking-wider">Position</div>
                <div className="text-white font-mono">#{row.queue_position}</div>
              </div>
              <div className="col-span-3 text-right flex items-center justify-end gap-1.5 text-[#F0B90B]">
                <Clock className="w-3 h-3" />
                <div>
                  <div className="text-[10px] text-[#848E9C] uppercase tracking-wider">
                    ETA <span className="text-[#848E9C]/60">· {row.age_minutes != null ? `waited ${fmtEta(row.age_minutes)}` : ''}</span>
                  </div>
                  <div className="font-mono font-semibold">{fmtEta(row.eta_minutes)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
